import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import { AlertChip, EstadoChip } from "@/components/AlertChip";
import { ArrowLeft, ArrowUp, ArrowDown, CircleNotch } from "@phosphor-icons/react";

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2.5 border-b border-[#E2E8F0] last:border-0">
      <span className="text-sm text-[#64748B]">{label}</span>
      <span className="text-sm font-semibold text-[#0F172A]">{value}</span>
    </div>
  );
}

const PAGO_STYLES = {
  pagado: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  abono: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  pendiente: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
};
const PAGO_LABEL = { pagado: "Pagado", abono: "Abono parcial", pendiente: "Pendiente" };

function PagoChip({ estado }) {
  const e = estado || "pendiente";
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${PAGO_STYLES[e]}`} data-testid="lote-pago-chip">
      {PAGO_LABEL[e]}
    </span>
  );
}

export default function LoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/lotes/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  const { lote, movimientos } = data;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="lote-detail-page">
      <button
        data-testid="back-button"
        onClick={() => (lote.producto_id ? navigate(`/productos/${lote.producto_id}`) : navigate("/productos"))}
        className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors"
      >
        <ArrowLeft size={18} weight="bold" /> Volver al producto
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl font-black tracking-tight text-[#0F172A]">Lote {lote.numero_lote}</h1>
            <EstadoChip estado={lote.estado} />
            {lote.fecha_vencimiento && <AlertChip level={lote.alert_level} dias={lote.dias_hasta_vencimiento} />}
          </div>
          <p className="text-[#475569] mt-1">{lote.producto_nombre} · {lote.codigo} · {lote.tipo}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-md p-6">
          <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-3">Información del lote</h2>
          <InfoRow label="Producto" value={lote.producto_nombre} />
          <InfoRow label="Proveedor" value={lote.proveedor_nombre || "—"} />
          <InfoRow label="Importación" value={fmtDate(lote.fecha_importacion)} />
          <InfoRow label="Vencimiento" value={lote.fecha_vencimiento ? fmtDate(lote.fecha_vencimiento) : "Sin vencimiento"} />
          <InfoRow label="Cantidad inicial" value={fmtNumber(lote.cantidad_inicial)} />
          <InfoRow label="Stock actual" value={fmtNumber(lote.stock_actual)} />
          <InfoRow label="Costo unitario" value={fmtMoney(lote.costo_unitario)} />
          <InfoRow label="Costo por embalaje" value={fmtMoney(lote.costo_embalaje)} />
          {lote.moneda && lote.tipo_cambio && lote.tipo_cambio !== 1 && (
            <>
              <InfoRow label="Moneda de compra" value={`${lote.moneda} (TC ${fmtNumber(lote.tipo_cambio)})`} />
              <InfoRow label="Costo unitario (origen)" value={fmtMoney(lote.costo_unitario_origen, lote.moneda)} />
              <InfoRow label="Costo embalaje (origen)" value={fmtMoney(lote.costo_embalaje_origen, lote.moneda)} />
            </>
          )}
          <InfoRow label="Valor en stock" value={fmtMoney(lote.valor)} />
          {(lote.numero_factura || lote.factura_bruto) && (
            <>
              <InfoRow label="Nº factura" value={lote.numero_factura || "—"} />
              <InfoRow label="Monto neto" value={lote.factura_neto != null ? fmtMoney(lote.factura_neto, lote.moneda) : "—"} />
              <InfoRow label="IVA" value={lote.factura_iva != null ? fmtMoney(lote.factura_iva, lote.moneda) : "—"} />
              <InfoRow label="Monto bruto" value={fmtMoney(lote.factura_bruto, lote.moneda)} />
              {lote.factura_vencimiento && <InfoRow label="Vence factura" value={fmtDate(lote.factura_vencimiento)} />}
            </>
          )}
          <InfoRow label="Estado de pago" value={<PagoChip estado={lote.estado_pago} />} />
          {lote.estado_pago === "abono" && (
            <>
              <InfoRow label="Abonado" value={fmtMoney(lote.monto_abonado, lote.moneda)} />
              <InfoRow label="Saldo pendiente" value={fmtMoney(lote.saldo, lote.moneda)} />
            </>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <h2 className="font-heading text-lg font-bold text-[#0F172A]">Historial de movimientos</h2>
          </div>
          {movimientos.length === 0 ? (
            <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lote-movimientos-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                    <th className="px-6 py-3 font-semibold">Tipo</th>
                    <th className="px-6 py-3 font-semibold">Motivo</th>
                    <th className="px-6 py-3 font-semibold text-right">Cantidad</th>
                    <th className="px-6 py-3 font-semibold text-right">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => (
                    <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-bold ${
                            m.tipo === "ingreso" ? "text-[#065F46]" : "text-[#991B1B]"
                          }`}
                        >
                          {m.tipo === "ingreso" ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
                          {m.tipo === "ingreso" ? "Ingreso" : "Salida"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-[#475569]">{m.motivo}</td>
                      <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                      <td className="px-6 py-3 text-right text-[#64748B]">{fmtDate(m.fecha_movimiento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
